import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { ShieldAlert, FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function Claims() {
  const [claims, setClaims] = useState([]);
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/claims"), api.get("/jobs")]).then(([c, j]) => {
      setClaims(c.data);
      setJobs(j.data);
    });
  }, []);

  // Derive pseudo-claims from jobs even if no claim records (for UX)
  const allClaims = jobs.filter((j) => j.claim_number).map((j) => ({
    id: j.id,
    insurance_company: j.insurance_company,
    claim_number: j.claim_number,
    status: j.status === "paid" ? "approved" : j.status === "completed" ? "approved" : "pending",
    job_id: j.id,
    job_vin: j.vin,
    vehicle: `${j.vehicle_year} ${j.vehicle_make} ${j.vehicle_model}`,
    total: j.total_amount,
    adas_required: (j.adas_analysis?.required_operations || []).length,
  }));

  return (
    <div className="space-y-6 animate-slide-up" data-testid="claims-page">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">Insurance Operations</div>
        <h1 className="font-display text-4xl font-semibold tracking-tight">Insurance Claims</h1>
        <p className="text-sm text-slate-400 mt-1.5">Active and historical claims across all carriers.</p>
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#0B1120] overflow-hidden">
        {allClaims.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">
            <ShieldAlert className="w-10 h-10 mx-auto mb-3 opacity-50" />
            No claims yet
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-900/40">
                <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Claim #</th>
                <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Insurer</th>
                <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Vehicle / VIN</th>
                <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Status</th>
                <th className="text-right text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">ADAS Ops</th>
                <th className="text-right text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Total</th>
              </tr>
            </thead>
            <tbody>
              {allClaims.map((c) => (
                <tr key={c.id} className="border-b border-slate-800/60 hover:bg-slate-800/20" data-testid={`claim-row-${c.id}`}>
                  <td className="px-5 py-3.5 font-mono text-xs text-blue-300">{c.claim_number}</td>
                  <td className="px-5 py-3.5 text-sm">{c.insurance_company}</td>
                  <td className="px-5 py-3.5 text-sm">
                    {c.vehicle}
                    <div className="text-xs font-mono text-slate-500">{c.job_vin}</div>
                  </td>
                  <td className="px-5 py-3.5">
                    <Badge className={`uppercase text-[10px] tracking-wider border ${
                      c.status === "approved" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" : "bg-amber-500/20 text-amber-300 border-amber-500/30"
                    }`}>{c.status}</Badge>
                  </td>
                  <td className="px-5 py-3.5 text-right font-mono text-sm text-cyan-400">{c.adas_required}</td>
                  <td className="px-5 py-3.5 text-right font-mono text-sm text-emerald-400">${c.total.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
